import { useField } from 'formik';
import { ReactElement, useEffect, useMemo } from 'react';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { PickerInput } from '../../../../shared/component/widget/picker-input';
import { PickerOption } from '../../../../shared/component/widget/widget.types';
import {
  ApplicationDefaultParameterSetFormTags,
  ApplicationDefaultParameterSetTag,
} from '../../../../shared/model/flow.types';
import { getAvailableApplicationParameterSetTagOptions } from '../../../../shared/util/flow-item.util';
import { BeaconCuttingData } from '../../model/beacon-cutting-data';

interface CutApplicationTagPickerProps {
  process: BeaconCuttingData.CutProcessValue | undefined;
  tagName: string;
  tag: ApplicationDefaultParameterSetTag;
  tags: ApplicationDefaultParameterSetFormTags | undefined;
  readOnly?: boolean;
}

const CutApplicationTagPicker = (
  props: CutApplicationTagPickerProps
): ReactElement => {
  const { process, tag, tagName, tags, readOnly } = props;

  const fieldName = `tags.${tagName}`;

  const [field, , helper] = useField(fieldName);

  const value = tags ? tags[tagName] : undefined;

  let isDisabled = false;

  const validOptions = useMemo(() => {
    if (tag.dependency === undefined) {
      return tag.options;
    }

    return isDisabled || process === undefined
      ? []
      : getAvailableApplicationParameterSetTagOptions(tag.options, {
          ...tags,
          process,
        });
  }, [isDisabled, process, tag.dependency, tag.options, tags]);

  const selectOptions: PickerOption[] = validOptions.map((option) => {
    return {
      label: option.label,
      value: option.value,
    };
  });

  // determine whether to disable the tags
  if (tag.dependency !== undefined) {
    if (tags === undefined) {
      isDisabled = true;
    } else if (
      tag.dependency === BeaconCuttingData.FlowItemParameterRef.PROCESS
    ) {
      isDisabled = !process;
    } else {
      isDisabled =
        !(tag.dependency in tags) ||
        tags[tag.dependency] === undefined ||
        selectOptions.length === 0;
    }
  }

  // reset values with dependencies when other values change
  useEffect(() => {
    if (tag.dependency === undefined) {
      return;
    }

    const valueIsValid =
      validOptions.find((opt) => opt.value === field.value) !== undefined;

    if (
      (isDisabled && field.value !== undefined) ||
      (field.value !== undefined && !valueIsValid)
    ) {
      helper.setValue(undefined);
    }
  }, [
    field.name,
    field.value,
    helper,
    isDisabled,
    tag.dependency,
    tags,
    validOptions,
  ]);

  return (
    <PickerInput
      name={fieldName}
      value={value}
      options={selectOptions}
      label={getLocalizedText(tag.label)}
      disabled={isDisabled || readOnly}
      placeholder={getLocalizedText('common:make_selection')}
    />
  );
};

export { CutApplicationTagPicker };
